import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import {
  formControlChanges$,
  TypedFormArray,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import {
  AccBooleanValue,
  AccIncapacityType,
  AccPhysicalRestriction,
  AccWorkTypeCode,
  IAccDateRange,
  IAccIncapacity,
  IAccSelectedAlternativeWork,
  IAccWorkCapacity,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  ISODateType,
  ITimePeriod,
  toISODate,
} from '@principle-theorem/shared';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { hasRequiredError, setRequiredValidation } from './form-group-helpers';

export class AccWorkCapacityFormGroup extends TypedFormGroup<IAccWorkCapacity> {
  constructor() {
    super({
      canResumeNormalWork: new TypedFormControl<AccBooleanValue>(
        AccBooleanValue.False
      ),
      incapacity: new TypedFormArray<IAccIncapacity>([]),
      returnToNormalWorkDate: new TypedFormControl<ISODateType>(undefined),
    });

    this._setupIncapacityFormHandlers();
  }

  patch(data: IAccWorkCapacity): void {
    this.controls.canResumeNormalWork.setValue(data.canResumeNormalWork);
    this.controls.returnToNormalWorkDate.setValue(data.returnToNormalWorkDate);

    if (data.incapacity) {
      this.incapacityFormArray.clear();

      data.incapacity.forEach((item) =>
        this.incapacityFormArray.push(new AccIncapacityFormGroup(item))
      );
    }
  }

  addIncapacityForm(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.incapacityFormArray.push(new AccIncapacityFormGroup());
  }

  removeIncapacityForm(event: Event, index: number): void {
    event.preventDefault();
    event.stopPropagation();
    const form = this.incapacityFormArray.at(index) as AccIncapacityFormGroup;
    form.destroy();
    this.incapacityFormArray.removeAt(index);
  }

  // Getters
  get incapacityFormArray(): TypedFormArray<IAccIncapacity> {
    return this.controls.incapacity as TypedFormArray<IAccIncapacity>;
  }

  get incapacityControls(): AccIncapacityFormGroup[] {
    return this.incapacityFormArray.controls as AccIncapacityFormGroup[];
  }

  get canResumeNormalWorkRequiredError(): boolean {
    return hasRequiredError(this.controls.canResumeNormalWork);
  }

  get displayIncapacity(): boolean {
    return this.controls.canResumeNormalWork.value === AccBooleanValue.False;
  }

  private _setupIncapacityFormHandlers(): void {
    formControlChanges$(this.controls.canResumeNormalWork)
      .pipe(filterUndefined(), takeUntilDestroyed())
      .subscribe((value) => {
        const hasControl = this.incapacityFormArray.length > 0;

        if (!hasControl && value === AccBooleanValue.False) {
          this.incapacityFormArray.push(new AccIncapacityFormGroup());
          return;
        }

        if (hasControl && value === AccBooleanValue.True) {
          this.incapacityFormArray.clear();
        }
      });
  }
}

class AccIncapacityFormGroup extends TypedFormGroup<IAccIncapacity> {
  private readonly destroy$ = new Subject<void>();

  constructor(data?: IAccIncapacity) {
    super({
      incapacityType: new TypedFormControl<AccIncapacityType>(
        undefined,
        Validators.required
      ),
      incapacityDateRange: new TypedFormControl<IAccDateRange>(
        undefined,
        Validators.required
      ),
      selectedAlternativeWork: new AccSelectedAlternativeWorkFormGroup(),
    });

    if (data) {
      this.patchValue(data);
    }

    this.controls.incapacityType.valueChanges
      .pipe(filterUndefined(), takeUntil(this.destroy$))
      .subscribe((value) => {
        if (value === AccIncapacityType.FitForSelectedWork) {
          this.selectedAlternativeWorkGroup.setRequired(true);
          return;
        }
        this.selectedAlternativeWorkGroup.setRequired(false);
        this.selectedAlternativeWorkGroup.reset();
      });
  }

  destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  updateIncapacityDateRange(dateRange: ITimePeriod): void {
    this.controls.incapacityDateRange.setValue({
      fromDate: toISODate(dateRange.from),
      toDate: toISODate(dateRange.to),
    });
  }

  get incapacityTypeRequiredError(): boolean {
    return hasRequiredError(this.controls.incapacityType);
  }

  get incapacityDateRangeRequiredError(): boolean {
    return hasRequiredError(this.controls.incapacityDateRange);
  }

  get selectedAlternativeWorkGroup(): AccSelectedAlternativeWorkFormGroup {
    return this.controls
      .selectedAlternativeWork as AccSelectedAlternativeWorkFormGroup;
  }

  get displaySelectedAlternativeWork(): boolean {
    return (
      this.controls.incapacityType.value ===
      AccIncapacityType.FitForSelectedWork
    );
  }
}

class AccSelectedAlternativeWorkFormGroup extends TypedFormGroup<IAccSelectedAlternativeWork> {
  constructor() {
    super({
      alternativeWorkTypeCode: new TypedFormControl<AccWorkTypeCode>(undefined),
      physicalRestrictions: new TypedFormControl<AccPhysicalRestriction[]>(
        undefined
      ),
      restrictedHoursPerDay: new TypedFormControl<number>(undefined),
      restrictionComment: new TypedFormControl<string>(undefined),
    });
  }

  setRequired(required: boolean): void {
    setRequiredValidation(required, this.controls.alternativeWorkTypeCode);
    setRequiredValidation(required, this.controls.restrictedHoursPerDay);
  }

  get alternativeWorkTypeCodeRequiredError(): boolean {
    return hasRequiredError(this.controls.alternativeWorkTypeCode);
  }

  get restrictedHoursPerDayRequiredError(): boolean {
    return hasRequiredError(this.controls.restrictedHoursPerDay);
  }
}
